"use client";

import { Strategy, useStrategiesStore } from "@/stores/strategiesStore";
import { StrategyCard } from "./StrategyCard";
import { StrategiesTableView } from "./StrategiesTableView";

interface StrategiesListProps {
  strategies: Strategy[];
  isLoading: boolean;
  onAllocate: (strategyId: string) => void;
  onManage: (strategyId: string) => void;
  isViewOnly?: boolean;
}

export const StrategiesList = ({
  strategies,
  isLoading,
  onAllocate,
  onManage,
  isViewOnly = false,
}: StrategiesListProps) => {
  const { viewMode } = useStrategiesStore();

  if (isLoading) {
    return (
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-64 animate-pulse rounded-xl bg-slate-800" />
        ))}
      </div>
    );
  }

  if (strategies.length === 0) {
    return (
      <div className="rounded-xl border border-slate-700 bg-slate-800 py-16 text-center">
        <p className="text-lg font-medium text-slate-400">
          No strategies match your filters
        </p>
        <p className="mt-1 text-sm text-slate-500">Try adjusting your filter criteria</p>
      </div>
    );
  }

  // On mobile (< md), always show cards since the table is too wide
  // The table is hidden on mobile via CSS, cards shown as fallback
  if (viewMode === "list") {
    return (
      <>
        {/* Table view (hidden on mobile) */}
        <div className="hidden md:block">
          <StrategiesTableView
            strategies={strategies}
            onAllocate={onAllocate}
            onManage={onManage}
            isViewOnly={isViewOnly}
          />
        </div>
        {/* Card fallback on mobile */}
        <div className="grid gap-6 md:hidden">
          {strategies.map((strategy) => (
            <StrategyCard
              key={strategy.id}
              strategy={strategy}
              onAllocate={onAllocate}
              onManage={onManage}
              isViewOnly={isViewOnly}
            />
          ))}
        </div>
      </>
    );
  }

  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {strategies.map((strategy) => (
        <StrategyCard
          key={strategy.id}
          strategy={strategy}
          onAllocate={onAllocate}
          onManage={onManage}
        />
      ))}
    </div>
  );
};
